import { MessageCircle, Search, UserCircle } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppLogin } from '../../hooks/useAppLogin';
import { hidesAgentBubble } from '../Agent/agentBubbleRoutes';
import { useAgentPanel } from '../Shell/AgentPanelContext';
import HomeButton from '../Shell/HomeButton';
import { useShell } from '../Shell/ShellContext';
import { pageTitle, useShowsBarTitle } from '../Shell/usePageTitle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const SITE_LINKS = [
  { name: 'About Saarim', url: 'https://saarim.me' },
  { name: 'Contact', url: 'mailto:saarim@berkeley.edu' },
  { name: 'Support Me', url: 'https://ko-fi.com/saarim' },
  { name: 'Privacy Policy', url: '/privacy' },
  { name: 'Terms and Conditions', url: '/terms' },
];

const SHORTCUT_LABEL = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K';

function Header() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const login = useAppLogin();
  const user = useSelector((state: any) => state.user);
  const { openTools, openFeedback } = useShell();
  const { open: agentOpen, setOpen: setAgentOpen } = useAgentPanel();
  const signedIn = Boolean(user?.email);
  const title = pageTitle(pathname, search);
  const showsTitle = useShowsBarTitle(`${pathname}${search}`);
  // Home has its own search field on a phone.
  const onHome = title === 'Home';

  return (
    <TooltipProvider delayDuration={300}>
      <header className="sticky top-0 z-50 grow-0 border-b border-border bg-black/55 shadow-none backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-screen-xl items-center gap-1 px-1.5 sm:px-6 md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,340px)_minmax(0,1fr)] md:gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <HomeButton className="sm:-ml-2" />
            {/* The page keeps its h1, so a screen reader does not need this copy. */}
            <span
              aria-hidden
              className={cn(
                'min-w-0 truncate text-[0.9375rem] font-semibold leading-tight text-white transition-opacity duration-150 motion-reduce:transition-none',
                showsTitle ? 'opacity-100' : 'opacity-0'
              )}
            >
              {title}
            </span>
          </div>

          <button
            type="button"
            onClick={openTools}
            className="hidden h-9 w-full items-center gap-2 rounded-lg bg-white/[0.05] px-3 text-sm text-white/60 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] transition-colors hover:bg-white/[0.08] hover:text-white/80 md:flex"
          >
            <Search aria-hidden className="size-4" />
            Search tools
            <kbd className="ml-auto font-mono text-xs">{SHORTCUT_LABEL}</kbd>
          </button>

          <div className="flex shrink-0 items-center justify-end gap-0.5">
            {onHome ? null : (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Search tools"
                onClick={openTools}
                className="text-white/90 hover:bg-white/5 md:hidden [&_svg]:size-5"
              >
                <Search />
              </Button>
            )}

            {hidesAgentBubble(pathname) ? null : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={agentOpen ? 'Close SaarimGPT' : 'Open SaarimGPT'}
                    aria-expanded={agentOpen}
                    aria-controls="agent-panel"
                    onClick={() => setAgentOpen((current) => !current)}
                    className={cn('[&_svg]:size-5', agentOpen ? 'bg-white/10 text-white' : 'text-white/90 hover:bg-white/5')}
                  >
                    <MessageCircle />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{agentOpen ? 'Close SaarimGPT' : 'Ask SaarimGPT'}</TooltipContent>
              </Tooltip>
            )}

            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Account and site menu"
                      className="text-white/90 hover:bg-white/5 [&_svg]:size-5"
                    >
                      <UserCircle />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>Account and site menu</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="end" className="w-52">
                {signedIn ? (
                  <DropdownMenuItem onClick={() => navigate('/profile')}>Profile</DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => login()}>Log In</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {SITE_LINKS.map((link) => (
                  <DropdownMenuItem key={link.name} asChild>
                    {link.url.startsWith('/') ? (
                      <Link to={link.url} className="text-inherit no-underline">
                        {link.name}
                      </Link>
                    ) : (
                      <a
                        href={link.url}
                        target={link.url.startsWith('http') ? '_blank' : undefined}
                        rel="noopener noreferrer"
                        className="text-inherit no-underline"
                      >
                        {link.name}
                      </a>
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={openFeedback}>Report an Issue</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
    </TooltipProvider>
  );
}

export default Header;
